import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import {
  CURRENCIES,
  type Currency,
  loadCurrency,
  saveCurrency,
} from "../lib/currency";
import { CurrencyPicker } from "../components/CurrencyPicker";
import { SeoForPage } from "../components/Seo";
import {
  createCheckoutSession,
  createPortalSession,
  getEntitlement,
  type Entitlement,
} from "../lib/api";
import { displayName, useSession } from "../lib/auth";
import { useT, useLp } from "../lib/i18n";
import { getAttribution } from "../lib/attribution";

/**
 * Signed-in dashboard — shows the user's current plan, both plan tiers
 * side-by-side, and a single CTA: either "upgrade to Pro" (if on Free)
 * or "manage subscription" (if on Pro). The Free card never has a button —
 * it's the default tier; there's nothing to switch *to* from Pro beyond
 * cancelling, which the Stripe Customer Portal handles.
 *
 * Gated behind auth — anonymous visitors are pushed to the login page so
 * the post-login redirect can drop them back here.
 */
export function Account() {
  const t = useT();
  const lp = useLp();
  const navigate = useNavigate();
  const session = useSession();
  const ta = t.account;

  // Redirect anonymous visitors back to login. Rendering a stub while the
  // navigation happens keeps the body empty rather than briefly flashing
  // the dashboard with zero data.
  useEffect(() => {
    if (!session) navigate(lp + "/?auth=login", { replace: true });
  }, [session, navigate, lp]);

  const [currency, setCurrency] = useState<Currency>("EUR");
  useEffect(() => setCurrency(loadCurrency()), []);

  const handleCurrencyChange = (next: Currency) => {
    setCurrency(next);
    saveCurrency(next);
  };

  const [cycle, setCycle] = useState<"monthly" | "annual">("annual");

  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    getEntitlement(session.tokens.access_token)
      .then((e) => { if (!cancelled) setEntitlement(e); })
      .catch(() => { /* entitlement endpoint may be briefly unreachable — UI still renders from `null` */ });
    return () => { cancelled = true; };
  }, [session]);

  const isPro = entitlement?.plan === "pro";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProAction = async () => {
    if (!session || busy) return;
    setBusy(true);
    setError(null);
    try {
      const attr = getAttribution();
      const res = isPro
        ? await createPortalSession(session.tokens.access_token)
        : await createCheckoutSession(session.tokens.access_token, { cycle, ...attr });
      window.location.assign(res.url);
    } catch {
      setError(ta.error);
      setBusy(false);
    }
  };

  if (!session) return null;

  const { free, pro } = t.pricing;
  const currencyInfo = CURRENCIES[currency];
  const proPrice = cycle === "annual"
    ? currencyInfo.proAnnualMonthlyFormatted
    : currencyInfo.proFormatted;
  const proPriceSubline = cycle === "annual"
    ? pro.annualBilledAs(
        currencyInfo.proAnnualTotalFormatted,
        currencyInfo.proAnnualSavingsPct,
      )
    : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <SeoForPage page="account" noindex />
      <Header />

      <main className="flex-1">
        <section className="mx-auto max-w-4xl px-6 py-16">
          {/* Heading */}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
              {ta.heading}
            </h1>
            <p className="mt-2 text-sm text-ink/60 dark:text-paper/60">
              {displayName(session)} · {session.email}
            </p>
          </div>

          {/* Current-plan banner */}
          <div className="mt-10 flex items-center justify-between gap-4 rounded-xl border border-margin dark:border-paper/10 bg-margin/40 dark:bg-paper/[0.03] px-5 py-4">
            <p className="text-sm text-ink/80 dark:text-paper/80">
              {ta.youAreOn}{" "}
              <span className="font-semibold text-ink dark:text-paper">
                {isPro ? pro.label : free.label}
              </span>{" "}
              {ta.plan}
            </p>
            <span
              className={[
                "inline-flex items-center rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider",
                isPro
                  ? "bg-graphite text-paper"
                  : "bg-ink/8 text-ink/70 dark:bg-paper/10 dark:text-paper/60",
              ].join(" ")}
            >
              {isPro ? pro.label : free.label}
            </span>
          </div>

          {/* Plan cards — only the Pro card has a CTA; Free is purely informational. */}
          <div className="mt-8 flex items-end justify-between flex-wrap gap-4">
            <p className="text-xs text-ink/50 dark:text-paper/50 font-mono uppercase tracking-wider">
              {t.pricing.label}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Billing cycle toggle — mirrors the public Pricing component so
                  Pro users (already subscribed) and Free users (about to
                  upgrade) see the same affordance. The cycle only flows into
                  the checkout call; portal-bound users land on Stripe's
                  billing portal where Stripe owns the cycle UI. */}
              <div
                role="group"
                aria-label="Billing cycle"
                className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 p-0.5 text-xs font-mono"
              >
                <button
                  type="button"
                  onClick={() => setCycle("monthly")}
                  className={[
                    "rounded-full px-4 py-1.5 transition-colors",
                    cycle === "monthly"
                      ? "bg-graphite text-paper"
                      : "text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper",
                  ].join(" ")}
                >
                  {pro.cycleMonthly}
                </button>
                <button
                  type="button"
                  onClick={() => setCycle("annual")}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors",
                    cycle === "annual"
                      ? "bg-graphite text-paper"
                      : "text-ink/60 dark:text-paper/60 hover:text-ink dark:hover:text-paper",
                  ].join(" ")}
                >
                  {pro.cycleAnnual}
                  <span
                    className={[
                      "font-mono text-[10px] tracking-tight",
                      cycle === "annual" ? "text-paper/70" : "text-graphite dark:text-graphite-muted",
                    ].join(" ")}
                  >
                    {pro.savingsBadge(currencyInfo.proAnnualSavingsPct)}
                  </span>
                </button>
              </div>
              <CurrencyPicker value={currency} onChange={handleCurrencyChange} />
            </div>
          </div>

          <div className="mt-4 grid gap-6 md:grid-cols-2">
            {/* Free — no button, by design. Dimmed when the user is on Pro to
                make the "this is the baseline you already surpassed" read
                obvious, highlighted otherwise. */}
            <PlanCard
              label={free.label}
              price="0"
              period={free.period}
              tagline={free.tagline}
              features={free.features}
              current={!isPro}
              note={!isPro ? ta.freeNote : undefined}
            />

            {/* Pro — button semantics flip based on entitlement. */}
            <PlanCard
              label={pro.label}
              price={proPrice}
              period={pro.period}
              priceSubline={proPriceSubline}
              tagline={pro.tagline}
              features={pro.features}
              current={isPro}
              highlighted
              note={isPro ? ta.proNote : undefined}
              action={{
                label: busy
                  ? isPro ? ta.openingPortal : pro.ctaLoading
                  : isPro ? ta.currentBadge : pro.cta,
                onClick: handleProAction,
                disabled: busy,
                /* When the user is already Pro, the button is informational
                   — not clickable. The "manage subscription" link below
                   handles the portal flow so the primary row stays calm. */
                passive: isPro,
              }}
            />
          </div>

          {error && (
            <p className="mt-4 text-center text-sm text-rec">{error}</p>
          )}

          {/* Manage-subscription affordance for Pro users. Kept as a quiet
              link rather than a big button so the plan card stays "just a
              status indicator" per product direction. */}
          {isPro && !busy && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleProAction}
                className="text-sm text-ink/60 dark:text-paper/60 underline underline-offset-4 hover:text-ink dark:hover:text-paper transition-colors"
              >
                {ta.manageSubscription}
              </button>
            </div>
          )}

          <p className="mt-12 text-center text-xs text-ink/50 dark:text-paper/50">
            {t.pricing.footer}
          </p>

          <div className="mt-6 text-center">
            <Link
              to={lp + "/"}
              className="text-xs text-ink/50 dark:text-paper/50 hover:text-ink dark:hover:text-paper transition-colors"
            >
              ← {t.auth.shell.homeLabel}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

interface PlanCardProps {
  label: string;
  price: string;
  period: string;
  /** Optional caption under the price/period row — used for the annual "Billed annually €90 — save 17%" line. */
  priceSubline?: string;
  tagline: string;
  features: readonly string[];
  current: boolean;
  highlighted?: boolean;
  note?: string;
  action?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    /** Render the button as a non-interactive "current plan" pill. */
    passive?: boolean;
  };
}

function PlanCard({ label, price, period, priceSubline, tagline, features, current, highlighted, note, action }: PlanCardProps) {
  const borderClass = highlighted
    ? "border-2 border-graphite dark:border-graphite-muted bg-graphite/[0.02] dark:bg-graphite/[0.08]"
    : "border border-margin dark:border-paper/10";
  const dimmed = !current && !highlighted;

  return (
    <article className={`relative rounded-2xl ${borderClass} p-7 flex flex-col ${dimmed ? "opacity-80" : ""}`}>
      {current && (
        <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-graphite text-paper text-[10px] font-mono uppercase tracking-wider px-3 py-1">
          ✓
        </span>
      )}

      <header>
        <div className="font-mono text-xs uppercase tracking-wider text-ink/50 dark:text-paper/50">
          {label}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-4xl font-semibold tracking-tight text-ink dark:text-paper">
            {price}
          </span>
          <span className="text-ink/60 dark:text-paper/60 text-sm">
            {period}
          </span>
        </div>
        {priceSubline && (
          <p className="mt-1 text-xs text-ink/50 dark:text-paper/50">
            {priceSubline}
          </p>
        )}
        <p className="mt-2 text-sm text-ink/60 dark:text-paper/60">
          {tagline}
        </p>
      </header>

      <ul className="mt-6 space-y-2 text-sm text-ink/75 dark:text-paper/75 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {action && (
        action.passive ? (
          <div className="mt-6 inline-flex items-center justify-center rounded-full bg-graphite/10 dark:bg-graphite/20 text-ink/80 dark:text-paper/80 px-5 py-3 text-sm font-medium select-none">
            {action.label}
          </div>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className="mt-6 inline-flex items-center justify-center rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-3 text-sm font-medium"
          >
            {action.label}
          </button>
        )
      )}

      {note && (
        <p className="mt-5 text-xs text-ink/55 dark:text-paper/55 leading-relaxed">
          {note}
        </p>
      )}
    </article>
  );
}

function Check() {
  return (
    <svg
      className="mt-0.5 h-4 w-4 flex-shrink-0 text-graphite dark:text-graphite-muted"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3,8 7,12 13,4" />
    </svg>
  );
}
